import { useState } from 'react';
import { Container, Row, Col, Badge, Button, Card } from 'react-bootstrap';
import { motion } from 'motion/react';
import CountUp from 'react-countup';
import blogData from '../data/blogData.json';

const Blog = () => {
  const [activeFilter, setActiveFilter] = useState('All Stories')

  const getImage = (imageName) => {
    return require(`../assets/${imageName}`)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  }

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section-primary section-xl text-white position-relative overflow-hidden">
        <Container className="position-relative">
          <Row className="align-items-center">
            <Col lg={10} className="mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Badge bg="success" className="mb-3 fs-6">
                  {blogData.hero.badge}
                </Badge>
              </motion.div>
              <motion.h2 className="display-6 text-primary fw-bold mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {blogData.hero.title}
              </motion.h2>
              <motion.p className="lead mb-4 text-muted"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {blogData.hero.subtitle}
              </motion.p>
              <motion.div className="d-flex gap-3 justify-content-center flex-wrap"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="success" href="#">Explore Case Studies</Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="outline-success" href="#">Submit Your Story</Button>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="py-5">
        <Container>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Row className="text-center g-4">
              {blogData.stats.map((stat, index) => (
                <Col md={3} xs={6} key={stat.id}>
                  <motion.div variants={cardVariants} whileHover={{ y: -5, scale: 1.05, transition: { duration: 0.3 } }}
                  >
                    <h2 className="fw-bold mb-2">
                      {stat.value.includes('+') || stat.value.includes('%') ? (
                        stat.value
                      ) : (
                        <CountUp end={parseFloat(stat.value)} duration={2.5} enableScrollSpy scrollSpyOnce suffix={stat.value.includes('B') ? 'B+' : '+'} decimals={stat.value.includes('.') ? 1 : 0} />
                      )}
                    </h2>
                    <p className="mb-0">{stat.label}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Filter Section */}
      <section className="section-lg bg-light border-bottom">
        <Container>
          <motion.div className="d-flex gap-2 flex-wrap justify-content-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {blogData.filters.map((filter) => (
              <motion.div
                key={filter}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button variant={activeFilter === filter ? 'success' : 'outline-secondary'} onClick={() => setActiveFilter(filter)}>
                  {filter}
                </Button>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      {/* Featured Case Study */}
      <section className="section-lg">
        <Container>
          <Row className="mb-4">
            <Col xs={12} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Badge bg="warning" text="dark" className="mb-2">
                  {blogData.featured.badge}
                </Badge>
                <h2 className="fw-bold">{blogData.featured.title}</h2>
              </motion.div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                whileHover={{ y: -5, transition: { duration: 0.3 } }}
              >
                <Card className="border-0 shadow-lg overflow-hidden">
                  <Row className="g-0">
                    <Col lg={6}>
                      <img src={getImage(blogData.featured.image)} className="img-fluid h-100 object-fit-cover" alt="Case Study"/>
                    </Col>
                    <Col lg={6} className="bg-light">
                      <Card.Body className="h-100 d-flex flex-column justify-content-center">
                        <div className="mb-3">
                          <Badge bg="primary" className="me-2">{blogData.featured.category}</Badge>
                          <Badge bg="success">{blogData.featured.growth}</Badge>
                        </div>
                        <h3 className="fw-bold mb-3">{blogData.featured.headline}</h3>
                        <p className="text-muted mb-4">{blogData.featured.description}</p>
                        <Row className="g-3 mb-4">
                          {blogData.featured.metrics.map((metric, idx) => (
                            <Col xs={6} key={idx}>
                              <motion.div className={`border-start border-${metric.color} border-4 ps-3`}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                              >
                                <h4 className={`fw-bold text-${metric.color} mb-1`}>{metric.value}</h4>
                                <small className="text-muted">{metric.label}</small>
                              </motion.div>
                            </Col>
                          ))}
                        </Row>
                        <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                          <Button variant="success" size="lg" href="#">Read Full Case Study</Button>
                        </motion.div>
                      </Card.Body>
                    </Col>
                  </Row>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Case Studies Grid */}
      <section className="section-lg bg-light">
        <Container>
          <Row className="mb-4">
            <Col xs={12}>
              <motion.h2 className="fw-bold"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                Success Stories by Industry
              </motion.h2>
              <motion.p className="text-muted"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                Real results from real businesses
              </motion.p>
            </Col>
          </Row>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <Row className="g-4">
              {blogData.caseStudies.map((study) => (
                <Col lg={study.size === 'large' ? 8 : 4} key={study.id}>
                  <motion.div variants={cardVariants} whileHover={{ y: -10, transition: { duration: 0.3 } }}>
                    <Card className="border-0 shadow-sm h-100">
                      {study.size === 'large' ? (
                        <Row className="g-0 h-100">
                          <Col md={5}>
                            <img src={getImage(study.image)} className="img-fluid h-100 object-fit-cover" alt="Case Study"/>
                          </Col>
                          <Col md={7}>
                            <Card.Body className="p-4 h-100 d-flex flex-column">
                              <div className="mb-3">
                                <Badge bg="info" text="dark">{study.category}</Badge>
                                {study.badge && <Badge bg={study.badgeVariant} className="ms-2">{study.badge}</Badge>}
                              </div>
                              <h4 className="card-title fw-bold mb-3">{study.title}</h4>
                              <p className="card-text text-muted flex-grow-1">{study.description}</p>
                              <Row className="g-2 mb-3 small">
                                {study.metrics.map((metric, idx) => (
                                  <Col xs={4} key={idx}>
                                    <strong className={`text-${metric.color} d-block`}>{metric.value}</strong>
                                    <span className="text-muted">{metric.label}</span>
                                  </Col>
                                ))}
                              </Row>
                              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                <Button variant="outline-success" href="#">View Case Study →</Button>
                              </motion.div>
                            </Card.Body>
                          </Col>
                        </Row>
                      ) : (
                        <>
                          <Card.Img variant="top" src={getImage(study.image)} style={{ height: '200px', objectFit: 'cover' }} alt="Case Study"/>
                          <Card.Body className="d-flex flex-column">
                            <Badge bg={study.category === 'Healthcare' ? 'warning' : 'primary'} text={study.category === 'Healthcare' ? 'dark' : 'light'} className="mb-2 align-self-start">
                              {study.category}
                            </Badge>
                            <h5 className="card-title fw-bold mb-3">{study.title}</h5>
                            <div className="mb-3 small">
                              {study.metrics.map((metric, idx) => (
                                <div key={idx} className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">{metric.label}</span>
                                  <strong className={`text-${metric.color}`}>{metric.value}</strong>
                                </div>
                              ))}
                            </div>
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                              <Button variant={`outline-${study.category === 'Healthcare' ? 'warning' : 'primary'}`} className="mt-auto" href="#">Read More →</Button>
                            </motion.div>
                          </Card.Body>
                        </>
                      )}
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>

          <Row className="mt-5">
            <Col xs={12} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button variant="success" size="lg" className="px-5">Load More Case Studies</Button>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section-lg">
        <Container>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Row className="align-items-center">
              <Col lg={8} className="text-center text-lg-start">
                <h2 className="fw-bold mb-2">Ready to Write Your Success Story?</h2>
                <p className="mb-4 mb-lg-0">Let's work together to achieve remarkable results for your business</p>
              </Col>
              <Col lg={4} className="text-center text-lg-end">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                  <Button variant="primary" size="lg" className="d-block d-sm-inline-block mb-2 mb-sm-0 me-sm-2" href="#">Get Started</Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                  <Button variant="outline-success" size="lg" className="d-block d-sm-inline-block" href="#">Contact Us</Button>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>
    </>
  )
}

export default Blog;
